import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { TreeComponent, TreeNode, ITreeOptions } from 'angular-tree-component';
import { SplitComponent } from 'angular-split';
import { DocService, eDocType, ISearchNode, eTabs } from '../services/doc.service';
import { Router, ActivationEnd } from '@angular/router';
import { Subscription } from 'rxjs';


export interface IBreadCrumb {
	name: string;
	id: number;
}

@Component({
	selector: 'reference-component',
	templateUrl: './reference.component.html',
	styleUrls: ['./reference.component.scss']
})

export class ReferenceComponent implements OnInit {
	@ViewChild('splitter', { static: false }) _splitterComponent: SplitComponent;
	@ViewChild('tree', { static: false }) _treeComponent: TreeComponent;
	private _currentTreeNode: TreeNode;
	private _crumbs: IBreadCrumb[];
	private _treeOptions: ITreeOptions;
	private _eventSubscription: Subscription;
	
	constructor(private _docService: DocService, private _cdref: ChangeDetectorRef, private _router: Router) {
		this._docService.ShowSearch = true;
		this._treeOptions = {
			getChildren: this._docService.getChildren.bind(this._docService),
			animateSpeed: 0,
			animateAcceleration: 0,
			animateExpand: false,
			useVirtualScroll: false
		};
		this._docService.SearchResultObservable.subscribe(searchResult => { this.processSearchResult(searchResult); });
		this._eventSubscription = this._router.events.subscribe({next: this.processRoute.bind(this)});
	}

	ngOnInit() {
	}

	ngOnDestroy() {
		this._docService.ShowSearch = false;
		this._eventSubscription.unsubscribe();
	}
	
	ngAfterViewInit() {
		this._splitterComponent.direction = 'horizontal';
		this._splitterComponent.disabled = false;
		this._currentTreeNode = this._treeComponent.treeModel.getFirstRoot().data;
		if (!this._treeComponent.treeModel.getActiveNode()) {
			this._treeComponent.treeModel.getFirstRoot().setActiveAndVisible();
		}
	}

	processRoute(event: Event) {
		if (event instanceof ActivationEnd) {
			let api = (event as ActivationEnd).snapshot.queryParams['api'];
			if (api) {
				let searchNode: ISearchNode = {
					matches: 1,
					key: 'ixnetwork_restpy.testplatform.sessions.ixnetwork',
					name: '',
					summary: '',
					docType: eDocType.class
				};
				for (let piece of api.split('/').filter(Boolean)) {
					searchNode.key += '.' + piece;
				}
				setTimeout(() => this._docService.SetSearchResult(searchNode));
			}
		}
	}

	public get options(): ITreeOptions {
		return this._treeOptions;
	}

	async processSearchResult(searchResult: ISearchNode) {
		if (this._treeComponent) {
			this._docService.ShowLoader = true;
			this._treeComponent.treeModel.collapseAll();
			let start = undefined;
			for (let piece of searchResult.key.split('.')) {
				if (piece === 'ixnetwork_restpy') {
					continue;
				}
				let foundNode = this._treeComponent.treeModel.getNodeBy((node) => {
					if (node && node.data && node.data.name.toLowerCase() === piece.toLowerCase()) {
						return node;
					}
				}, start);
				if (start && foundNode && start.data.name == foundNode.data.name) {
					continue;
				} else if (foundNode && foundNode.data.docType === eDocType.class) {
					start = foundNode;
					await start.loadNodeChildren();
				} else if (start && searchResult.key.endsWith(piece)) {
					for (let subNode of start.children) {
						if (piece.toLowerCase() === subNode.data.name.toLowerCase()) {
							start = subNode;
							break;
						}
					}
					break;
				} else {
					break;
				}
			}
			if (start) {
				start.setActiveAndVisible();
			}
		}
		searchResult = null;
		this._docService.ShowLoader = false;
	}

	public getTreeNodeIcon(treeNode: TreeNode): string {
		if (treeNode && treeNode.data) {
			return this._docService.getDocNodeIcon(treeNode.data.docType);
		} else {
			return null;
		}
	}

	addCrumb(treeNode: TreeNode, crumbs: IBreadCrumb[]) {
		if (treeNode) {
			crumbs.unshift({
				name: treeNode.data.name,
				id: treeNode.id
			});
			this.addCrumb(treeNode.parent, crumbs);
		}
	}

	public get crumbs(): IBreadCrumb[] {
		return this._crumbs;
	}

	public onBreadCrumb(crumb: IBreadCrumb) {
		let node: TreeNode = this._treeComponent.treeModel.getNodeById(crumb.id);
		for (let child of node.children) {
			child.collapseAll();
		}
		node.setActiveAndVisible();
	}

	public onActivate(event: any) {
		if (event.node) {
			this._currentTreeNode = event.node;
			let crumbs: IBreadCrumb[] = [];
			this.addCrumb(this._currentTreeNode, crumbs);
			this._crumbs = crumbs;
			this._currentTreeNode.expand();
		}
		this._cdref.detectChanges();
	}

	addAddOrFind(currentClassNode: TreeNode, selectedClassNode: TreeNode): boolean {
		if (currentClassNode.data.name !== selectedClassNode.data.name) {
			return true;
		}
		if (this.currentTreeNode.data.docType !== eDocType.method) {
			return true;
		}
		if (this.currentTreeNode.data.doc.Signature.search(/^(add\(|find\()/) === -1) {
			return true;
		}
		return false;
	}

	public get sampleCode(): string {
		if (this._currentTreeNode && this._currentTreeNode.data) {
			let selectedClassNode = this._currentTreeNode;
			if (selectedClassNode.data.docType !== eDocType.class && selectedClassNode.data.docType !== eDocType.multivalue) {
				selectedClassNode = selectedClassNode.parent;
			}
			let classNode: TreeNode = this._currentTreeNode;
			if (classNode.data.docType !== eDocType.class && classNode.data.docType !== eDocType.multivalue) {
				classNode = this._currentTreeNode.parent;
			}
			let code: string[] = [];
			while (classNode && classNode.parent && classNode.parent.data && classNode.data.name !== 'TestPlatform') {
				let line: string = `\t.${classNode.data.name}`;
				let methods: any[] = classNode.data.doc.Methods;
				if (this.addAddOrFind(classNode, selectedClassNode)) {
					let add = methods.find(m => m.Signature.startsWith('add('));
					let find = methods.find(m => m.Signature.startsWith('find('));
					if (add || find) {
						line += `.find()`;
					}
                }
                if (classNode.data.name !== selectedClassNode.data.name) {
                    line += ' \\';
                }
				line += '\n';
				code.unshift(line)
				classNode = classNode.parent;
			}
			if (this._currentTreeNode.data.docType === eDocType.method) {
				code.push(`${this._currentTreeNode.parent.data.name.toLowerCase()}.${this._currentTreeNode.data.doc.Signature}\n`)
				if (this._currentTreeNode.data.name === 'add' || this._currentTreeNode.data.name === 'find') {
					code.push(`print(${selectedClassNode.data.name.toLowerCase()})\n`)
				}
			} else if (this._currentTreeNode.data.docType === eDocType.property) {
                code.push(`print(${this._currentTreeNode.parent.data.name.toLowerCase()}.${this._currentTreeNode.data.doc.Signature})\n`)
                if (this._currentTreeNode.data.doc.ReadWrite) {
                    let leftSide = `${this._currentTreeNode.parent.data.name.toLowerCase()}.${this._currentTreeNode.data.doc.Signature}`;
                    let rightSide =`'<str>'`;
                    code.push(`${leftSide} = ${rightSide} \n`) 
                }
			} else if (this._currentTreeNode.data.docType === eDocType.class && this._currentTreeNode.parent.data.name) {
				code.push(`print(${this._currentTreeNode.data.name.toLowerCase()})\n`)
			} else if (this._currentTreeNode.data.docType === eDocType.multivalue) {
				code.push(`print(${this._currentTreeNode.data.name.toLowerCase()})\n`)
			} else {
				code.push(`testplatform.info(testplatform)\n`)
			}

            if (selectedClassNode.data.id === 0) {
                code.unshift(`${selectedClassNode.data.name.toLowerCase()} = TestPlatform('127.0.0.1')\n`);
            } else {
                code.unshift(`${selectedClassNode.data.name.toLowerCase()} = TestPlatform('127.0.0.1') \\\n`);
            }
            code.unshift("from ixnetwork_restpy import TestPlatform\n\n\n");

            return code.join('');
		} else {
			return '';
		}
	}

	public get header(): string {
		if (this._currentTreeNode && this._currentTreeNode.data) {
            return this._currentTreeNode.data.name;
			switch (this._currentTreeNode.data.docType) {
				case eDocType.class:
				case eDocType.multivalue:
                    if (this._currentTreeNode.parent.data.name) {
                        return `${this._currentTreeNode.parent.data.name}.${this._currentTreeNode.data.name}`;
                    } else {
                        return this._currentTreeNode.data.name;
                    }
                case eDocType.method:
                    return `${this._currentTreeNode.parent.data.name}.${this._currentTreeNode.data.name}`;
				default:
					return `${this._currentTreeNode.parent.data.name}.${this._currentTreeNode.data.doc.Signature}`;
			}
		} else {
			return "";
		}
	}

	public get summary(): string {
		if (this._currentTreeNode && this._currentTreeNode.data) {
            let summary = '""" ';
            switch(this._currentTreeNode.data.docType) {
                case eDocType.class:
                case eDocType.property:
                    if (this._currentTreeNode.parent.data.name) {
                        summary += `Property ${this._currentTreeNode.parent.data.name}.${this._currentTreeNode.data.name}\n`;
                    } else {
                        summary += `Class ${this._currentTreeNode.data.name}\n`;
                    }
                    break;
                case eDocType.method:
                    summary += `Method ${this._currentTreeNode.parent.data.name}.${this._currentTreeNode.data.name}\n`;
                    break;
            }
            let lines: string[] = this._currentTreeNode.data.doc.Summary.split('\n');
            for (let index = 0; index < lines.length; index++) {
                let line = lines[index].trim();
                if (line.length == 0) {
                    continue;
                }
                if (index + 1 < lines.length && lines[index + 1].trim().startsWith('----')) {
                    summary += '\n';
                }
                summary += `\n${line}`;
            } //.replace(/\t/g, '').replace(/\"/g, '');
            summary += '\n"""\n';
            summary += '# This auto generated sample is based on the following assumptions:\n';
            summary += '# - there is at least one session active\n';
            summary += '# - the session configuration contains at least one resource for every class present in the sample\n';
			return summary;
		} else {
			return "";
		}
	}

	public get nodes(): any {
		return this._docService.rootNodes;
	}

	public get currentTreeNode(): TreeNode {
		return this._currentTreeNode;
	}

	public get deprecated(): boolean {
		return this._currentTreeNode && this._currentTreeNode.data.doc.Deprecated !== null && this._currentTreeNode.data.doc.Deprecated === true;
	}

	public get readWrite(): boolean {
		return this._currentTreeNode && this._currentTreeNode.data.doc.ReadWrite !== null && this._currentTreeNode.data.doc.ReadWrite === true;
	}

	public get readOnly(): boolean {
		return this._currentTreeNode && this._currentTreeNode.data.doc.ReadWrite !== null && this._currentTreeNode.data.doc.ReadWrite === false;
	}
}
